/**
 * Original-model preview — draws the parsed OBJ mesh with the same isometric
 * camera as the brick build, so the "original" and "bricks" views line up and
 * the user can see the model the bricks were derived from.
 *
 * Camera: a yaw orbit around the build's vertical axis, projected with the
 * same isometric axes as the brick renderer (grid x → (0.866, 0.5), grid y →
 * (−0.866, 0.5)), with the orbit pitch driving the vertical foreshortening.
 * Faces are painted back to front (painter's algorithm) and flat-shaded from
 * their normal, so the silhouette reads without a full 3D engine.
 */

import type { ObjMesh } from "../../utils/objImport";
import {
  LIGHT,
  cameraDepth,
  makeCamera,
  projectPoint,
  shadeColor,
  type Camera,
} from "../BrickShapeRenderer/renderers";

export interface MeshRotation {
  x: number;
  y: number;
}

export interface ProjectedVertex {
  /** screen x, before the bounds are centered on the canvas */
  sx: number;
  /** screen y, before the bounds are centered on the canvas */
  sy: number;
  /** camera depth — larger is nearer the viewer */
  depth: number;
  /** yaw-rotated build-space coordinates (for face normals) */
  wx: number;
  wy: number;
  wz: number;
}

/**
 * Project the mesh with the shared camera — the same view the brick build
 * is drawn from, so the two views of a model line up.
 */
export const projectMesh = (
  vertices: Array<[number, number, number]>,
  pixelSize: number,
  rotation: MeshRotation,
): ProjectedVertex[] => {
  const cam = makeCamera(rotation);

  return vertices.map(([x, y, z]) => {
    const p = projectPoint(cam, x * pixelSize, y * pixelSize, z * pixelSize);
    return {
      wx: x,
      wy: y,
      wz: z,
      sx: p.x,
      sy: p.y,
      depth: cameraDepth(cam, x * pixelSize, y * pixelSize, z * pixelSize),
    };
  });
};

export interface MeshRenderOptions {
  canvasSize: { width: number; height: number };
  /** screen pixels per grid unit — the same scale the brick build uses */
  pixelSize: number;
  rotation: MeshRotation;
  /** zoom around the canvas center */
  scale: number;
  /** base fill color */
  color: string;
}

interface Face {
  a: ProjectedVertex;
  b: ProjectedVertex;
  c: ProjectedVertex;
  depth: number;
  shade: number;
}

/** Face normal, flipped to face the viewer. */
const faceShade = (
  cam: Camera,
  a: ProjectedVertex,
  b: ProjectedVertex,
  c: ProjectedVertex,
): number => {
  const ux = b.wx - a.wx;
  const uy = b.wy - a.wy;
  const uz = b.wz - a.wz;
  const vx = c.wx - a.wx;
  const vy = c.wy - a.wy;
  const vz = c.wz - a.wz;

  let nx = uy * vz - uz * vy;
  let ny = uz * vx - ux * vz;
  let nz = ux * vy - uy * vx;

  // Point the normal at the camera so the shading never depends on the
  // model's winding order
  if (nx * cam.view[0] + ny * cam.view[1] + nz * cam.view[2] < 0) {
    nx = -nx;
    ny = -ny;
    nz = -nz;
  }

  const len = Math.hypot(nx, ny, nz) || 1;
  const lambert = Math.max(
    0,
    (nx * LIGHT[0] + ny * LIGHT[1] + nz * LIGHT[2]) / len,
  );
  // Keep the darkest faces readable rather than crushing them to black
  return 0.66 + 0.34 * lambert;
};

/**
 * Draw the mesh into `ctx`. The projected bounds are centered on the canvas
 * (so the model never draws off to one side) and zoomed around that center.
 */
export const renderMesh = (
  ctx: CanvasRenderingContext2D,
  mesh: ObjMesh,
  options: MeshRenderOptions,
): void => {
  const { canvasSize, pixelSize, rotation, scale, color } = options;
  const cam = makeCamera(rotation);
  const projected = projectMesh(mesh.vertices, pixelSize, rotation);
  if (projected.length === 0) return;

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of projected) {
    if (p.sx < minX) minX = p.sx;
    if (p.sx > maxX) maxX = p.sx;
    if (p.sy < minY) minY = p.sy;
    if (p.sy > maxY) maxY = p.sy;
  }
  const midX = (minX + maxX) / 2;
  const midY = (minY + maxY) / 2;
  const cx = canvasSize.width / 2;
  const cy = canvasSize.height / 2;
  const toScreen = (p: ProjectedVertex) => ({
    x: cx + (p.sx - midX) * scale,
    y: cy + (p.sy - midY) * scale,
  });

  const faces: Face[] = [];
  for (const [ia, ib, ic] of mesh.triangles) {
    const a = projected[ia];
    const b = projected[ib];
    const c = projected[ic];
    if (!a || !b || !c) continue;
    faces.push({
      a,
      b,
      c,
      depth: (a.depth + b.depth + c.depth) / 3,
      shade: faceShade(cam, a, b, c),
    });
  }
  // Painter's algorithm: farthest faces first, nearest last
  faces.sort((f1, f2) => f1.depth - f2.depth);

  for (const face of faces) {
    const p1 = toScreen(face.a);
    const p2 = toScreen(face.b);
    const p3 = toScreen(face.c);

    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.lineTo(p3.x, p3.y);
    ctx.closePath();
    ctx.fillStyle = shadeColor(color, face.shade);
    ctx.fill();
    // A hairline edge keeps neighbouring faces readable
    ctx.strokeStyle = "rgba(0,0,0,0.16)";
    ctx.lineWidth = 1;
    ctx.stroke();
  }
};
